import { useState } from 'react'
import { message as $message } from 'antd'
import cloneDeep from 'lodash/cloneDeep'
import arrayMove from 'array-move'
import { createAPI } from '@/utils/modal'
import AddQuestionTypeModal from '@/modules/afterSale/components/AddQuestionTypeModal'
import { listAllAfterSaleQuestion, saveAfterSaleQuestion } from '@/services/afterSale'

const MAX_LIST_LENGTH = 500

export interface ItemType {
    question: string
    systemQuestion?: boolean
    showValue?: boolean
}

export interface Option {
    label: string
    value: string
    key: string
    title: string
}

export interface AfterSalesQuestionType {
    // 【售后问题类型】列表
    list: ItemType[]
    // 编辑弹窗【售后问题类型】列表
    tempList: ItemType[]
    // 编辑弹窗【售后问题类型】列表勾选状态
    tempCheckedValue: Record<string, boolean>
    // 获取【售后问题类型】列表Loading
    loading: boolean
    // 更新【售后问题类型】列表Loading
    updateLoading: boolean
    // 获取【售后问题类型】列表
    fetchList: () => void
    // 更新【售后问题类型】列表
    updateList: () => Promise<void>
    // 编辑【售后问题类型】
    editAfterSalesQuestionType: () => void
    // 新增【售后问题类型】
    addAfterSalesQuestionType: (label: string) => boolean
    // 排序【售后问题类型】
    sortAfterSalesQuestionType: (oldIndex: number, newIndex: number) => void
    // 勾选/取消勾选【售后问题类型】
    checkAfterSalesQuestionType: (question: string, checked: boolean) => void
    // 删除【售后问题类型】
    deleteAfterSalesQuestionType: (question: string) => void
}

const useAfterSalesQuestionType: () => AfterSalesQuestionType = () => {
    const [list, setList] = useState<ItemType[]>([])
    const [tempList, setTempList] = useState<ItemType[]>([])
    const [tempCheckedValue, setTempCheckedValue] = useState<Record<string, boolean>>({})
    const [loading, setLoading] = useState<boolean>(true)
    const [updateLoading, setUpdateLoading] = useState(false)

    const fetchList = () => {
        setLoading(true)
        listAllAfterSaleQuestion()
            .then(response => {
                const { message, data, success } = response
                if (!success) {
                    $message.error(message)
                    return
                }
                setList(data || [])
            })
            .finally(() => setLoading(false))
    }

    const updateList = () => {
        const payload = {
            questions: tempList
                .filter(_ => (_.systemQuestion ? tempCheckedValue[_.question] : true))
                .map((_, index) => ({
                    question: _.question,
                    sort: index,
                    systemQuestion: _.systemQuestion,
                })),
        }
        return new Promise<void>((resolve, reject) => {
            setUpdateLoading(true)
            saveAfterSaleQuestion(payload)
                .then(res => {
                    const { success, message } = res
                    if (!success) {
                        $message.error(message)
                        reject(new Error(message))
                        return
                    }
                    setList(
                        tempList.map((_, index) => ({
                            showValue: _.systemQuestion ? tempCheckedValue[_.question] : true,
                            question: _.question,
                            sort: index,
                            systemQuestion: _.systemQuestion,
                        })),
                    )
                    resolve()
                })
                .catch(reject)
                .finally(() => setUpdateLoading(true))
        })
    }

    const editAfterSalesQuestionType = ({ onOk } = {}) => {
        setTempList(cloneDeep(list))
        setTempCheckedValue(
            list.reduce((result, item) => {
                result[item.question] = item.systemQuestion ? !!item.showValue : true
                return result
            }, {}),
        )
        setUpdateLoading(false)
        createAPI(AddQuestionTypeModal, {
            onOk,
        })
    }

    const addAfterSalesQuestionType = (label: string) => {
        if (!label) {
            return false
        }
        if (!/^[\u4e00-\u9fa5a-zA-Z\d\(\)（）\-\.\/\,\，\、]+$/.test(label)) {
            $message.error(`只能包含中文，英文，数字，横杠（-），点（.），正斜杠（/），顿号（、），小括号`)
            return false
        }
        if (tempList.length >= MAX_LIST_LENGTH) {
            $message.error(`最多自定义${MAX_LIST_LENGTH}个问题类型`)
            return false
        }
        if (tempList.find(_ => _.question === label)) {
            $message.error('问题类型不能重复')
            return false
        }
        setTempList(_ => [
            ..._,
            {
                question: label,
                systemQuestion: false,
            },
        ])
        return true
    }

    const sortAfterSalesQuestionType = (oldIndex: number, newIndex: number) => {
        setTempList(_ => arrayMove(_, oldIndex, newIndex))
    }

    const checkAfterSalesQuestionType = (question: string, checked: boolean) => {
        if (
            !checked &&
            tempList.every(_ => _.systemQuestion) &&
            Object.values(tempCheckedValue).filter(_ => !!_).length <= 1
        ) {
            $message.error('至少保留一个问题类型')
            return
        }
        setTempCheckedValue(_ => {
            return { ..._, [question]: checked }
        })
    }

    const deleteAfterSalesQuestionType = (question: string) => {
        if (
            tempList.filter(_ => _.systemQuestion).every(_ => !tempCheckedValue[_.question]) &&
            tempList.filter(_ => !_.systemQuestion).length <= 1
        ) {
            $message.error('至少保留一个问题类型')
            return
        }
        setTempList(_list => _list.filter(_ => _.question !== question))
        setTempCheckedValue(_ => {
            return { ..._, [question]: false }
        })
    }

    return {
        list,
        tempList,
        tempCheckedValue,
        loading,
        updateLoading,
        fetchList,
        updateList,
        editAfterSalesQuestionType,
        addAfterSalesQuestionType,
        sortAfterSalesQuestionType,
        checkAfterSalesQuestionType,
        deleteAfterSalesQuestionType,
    }
}

export default useAfterSalesQuestionType
